import { useMutation, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../fetcher"
import uuid from "react-native-uuid"
import { removeValue, storeValue } from "../../utils"
import { DEMO_PHONE_NUMBER } from "../../constants"
import { getDeviceId } from "../../utils/deviceId"

export type StartPhoneAuthResponse = {
  attemptId: string | null
  referenceId: string | null
  outcome: "OtpSent" | "Rejected"
  errors: string[]
  redirectUrl?: string | null
  linkToken?: {
    tokenId: string
    linkToken: string
    expiration: string
  } | null
}

type MutationFnParams = {
  phoneNumber: string
  registrationToken?: string
  /** Phone auth owns the error screen so the API message can be shown once. */
  disableErrorScreen?: boolean
}

export function useStartPhoneAuth() {
  const queryClient = useQueryClient()

  return useMutation<StartPhoneAuthResponse, unknown, MutationFnParams>({
    mutationFn: async ({ phoneNumber, registrationToken, disableErrorScreen }) => {
      if (phoneNumber?.replaceAll("-", "") === DEMO_PHONE_NUMBER) {
        await storeValue("mode", "demo")
      } else {
        await removeValue("mode")
      }

      const response = await fetcher(
        "/Auth/Phone/Start",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phoneNumber,
            registrationToken: registrationToken || uuid.v4(),
            deviceId: getDeviceId(),
          }),
        },
        { disableErrorScreen }
      )

      return response.json()
    },
    onSuccess: (response, variables) => {
      // Query cache only — these hooks mount above PlaidProvider, so context setters are no-ops.
      // Do not write attemptId to phoneNumberVerificationId: Plaid LinkToken/Layer
      // expect a verification id, which arrives later from /Auth/Phone/Confirm.
      queryClient.setQueryData(["signUpPhoneNumber"], variables.phoneNumber)
      queryClient.setQueryData(["onboardingLinkToken"], response.linkToken)
    },
  })
}
